import React from 'react';
import EventTitle from './EventTitle';
import EventCaseName from './EventCaseName';
import EventNotLinkedToCase from './EventNotLinkedToCase';
import EventType from './EventType';
import EventDate from './EventDate';
import EventAllDay from './EventAllDay';
import EventTime from './EventTime';
import EventRecurring from './EventRecurring';
import EventFirmWide from './EventFirmWide';
import EventLocation from './EventLocation';
import EventPracticeArea from './EventPracticeArea';
import EventAttendees from './EventAttendees';
import EventDescription from './EventDescription';
import EventAttachments from './EventAttachments';
import EventReminders from './EventReminders';

/**
 * EventForm - Combines all event form fields into a single form
 * This component is responsible for rendering all form fields in the correct order
 */
function EventForm({
  control,
  resetField,
  setValue,
  getValues,
  watch,
  errors,
  trigger,
  eventFormMode,
  isGlobal = true,
  attendeesList = [],
  isLoadingAttendees = false,
}) {
  const notLinkedToCaseWatcher = watch('not_linked_to_case');
  const allDay = watch('all_day');
  const firmWideWatcher = watch('firm_wide');

  // Watch attendee arrays to check if any have send_reminder enabled
  const contacts = watch('contacts') || [];
  const firmUsers = watch('firm_users') || [];
  const groups = watch('groups') || [];

  // Check if any attendee has send_reminder enabled
  const isReminderRequired = React.useMemo(() => {
    const hasContactWithReminder = contacts.some((contact) => contact?.send_reminder === true);
    const hasFirmUserWithReminder = firmUsers.some((user) => user?.send_reminder === true);
    const hasGroupWithReminder = groups.some((group) => group?.send_reminder === true);

    return hasContactWithReminder || hasFirmUserWithReminder || hasGroupWithReminder;
  }, [contacts, firmUsers, groups]);

  return (
    <form>
      {/* Form heading */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div className="modal-heading F-size16 text-bold">{eventFormMode === 'edit' ? 'Event Details' : 'Create Event'}</div>
      </div>

      {/* Event Title */}
      <EventTitle control={control} errors={errors} />

      {/* Case/Lead Selection */}
      {isGlobal && !firmWideWatcher && (
        <>
          <EventCaseName control={control} errors={errors} notLinkedToCaseWatcher={notLinkedToCaseWatcher} />
          <EventNotLinkedToCase control={control} setValue={setValue} isEditMode={eventFormMode === 'edit'} />
        </>
      )}

      {/* Event Type */}
      <EventType control={control} watch={watch} />

      {/* Date */}
      <EventDate {...{ control, trigger, errors, setValue, watch }} />

      {/* All Day Toggle */}
      <EventAllDay control={control} />

      {/* Time - Only show if not all day */}
      {!allDay && <EventTime {...{ control, trigger, errors, setValue, getValues, watch }} />}

      {/* Recurring Event */}
      <EventRecurring {...{ control, resetField, setValue, trigger, watch, isGlobal, errors, eventFormMode }} />

      {/* Firm-Wide Event (Admin only) */}
      {isGlobal && <EventFirmWide {...{ control, watch }} />}

      {/* Location & Conference */}
      <EventLocation {...{ control, setValue, watch, editEventModal: eventFormMode === 'edit', errors, trigger }} />

      {/* Practice Area - Only show if not linked to case */}
      {notLinkedToCaseWatcher && <EventPracticeArea control={control} />}

      {/* Attendees - Hide for firm-wide events */}
      {!firmWideWatcher && (
        <EventAttendees {...{ control, getValues, setValue, trigger, watch, attendeesList, isLoadingAttendees, errors }} />
      )}

      {/* Description */}
      <EventDescription control={control} />

      {/* Attachments */}
      <EventAttachments {...{ control, setValue, getValues, watch }} />

      {/* Reminders */}
      <EventReminders {...{ control, setValue, watch, isGlobal, errors, isRequired: isReminderRequired, trigger }} />
    </form>
  );
}

export default EventForm;
